import React, { useState } from 'react'
import InfoIcon from '@mui/icons-material/Info'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Input,
  InputAdornment,
  InputLabel,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import NormalText from 'common/Typography/NormalText'
import { FormikContextType, useFormikContext } from 'formik'
import OrbitSession from 'hooks/useCookies'

import { FormDetailsProps } from '../sharedDialogs/PartialRefundFormDialog'

export function RefundFormV3({
  customerEmail,
  setRefundAmount,
  isRefundLoading,
  handleRefundLoading,
  customerId,
  mutateRefund,
  amount,
  maxRefundAmount,
  currency,
  paymentId,
  refundType,
  totalpackDataUsedPercentage,
  maximumRefundPercentage
}: RefundFormProps) {
  const use2DPLookup: Record<string, boolean> = {
    USD: true,
    SGD: true,
    GBP: true,
    EUR: true,
    CAD: true,
    AUD: true,
    KRW: false,
    CNY: true,
    MNT: false,
    IDR: false,
    NZD: true,
    JPY: false
  }

  const formik: FormikContextType<FormDetailsProps> = useFormikContext()
  const faberToken = OrbitSession.get('faberToken')
  const username = OrbitSession.get('userName')

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isTerminated, setIsTerminated] = useState(true) // defaultChecked → true
  const openDialog = () => setIsDialogOpen(true)
  const closeDialog = () => setIsDialogOpen(false)
  const handleConfirm = () => {
    setIsDialogOpen(false)
    handleRefundLoading()
    handleSubmit()
  }

  const handleSubmit = () => {
    const payload = {
      faberToken,
      invoiceID: paymentId,
      refundReason: formik.values.refundReason,
      userId: customerId,
      ticketId: formik.values.ticketId,
      refundAmount: amount,
      currency,
      terminatePlan: isTerminated
    }

    if (mutateRefund) {
      mutateRefund({
        ...payload
      })
    }
    return
  }

  const handleOnBlurAmount = (input: string) => {
    const shouldUse2DP = handleUse2DP(currency ?? '')
    let parsed

    if (shouldUse2DP) {
      parsed = parseFloat(input)
    } else {
      parsed = parseInt(input)
    }

    if (isNaN(parsed)) {
      setRefundAmount('')
      return
    }
    const value = parsed.toFixed(2)

    setRefundAmount(value)
  }

  const handleUse2DP = (currency: string): boolean => {
    return use2DPLookup[currency] ?? false
  }

  const labelStyle =
    0 >= Number(amount) || Number(amount) > Number(maxRefundAmount)
      ? { fontSize: '1.2rem', fontWeight: 'bold', color: 'red' }
      : {}

  return (
    <Stack sx={{}}>
      <Dialog open={isDialogOpen} onClose={closeDialog}>
        <DialogTitle>Confirm Partial Refund</DialogTitle>
        <DialogContent>
          <NormalText>
            Are you sure you want to confirm this refund transaction?
          </NormalText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color='secondary'>
            Cancel
          </Button>
          <Button onClick={handleConfirm} color='primary'>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ py: 1, mt: 2 }}>
        <InputLabel shrink sx={labelStyle}>
          Maximum Refund Amount: {maxRefundAmount} {currency}
        </InputLabel>

        <TextField
          name='amount'
          type='number'
          placeholder='Input amount to refund'
          value={amount}
          onChange={(e) => setRefundAmount(e.target.value)}
          onBlur={(e) => handleOnBlurAmount(e.target.value)}
          fullWidth
          variant='outlined'
          sx={{ mt: 0, borderRadius: '8px' }}
          inputProps={{
            min: 0,
            max: maxRefundAmount,
            step: handleUse2DP(currency ?? '') ? 0.01 : 1.0,
            inputMode: 'decimal',
            readOnly: refundType === 'refund_full'
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>{currency}</InputAdornment>
            )
          }}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
            alignItems: 'center'
          }}
        >
          <InfoIcon
            sx={{
              fontSize: '14px',
              color: '#000868'
            }}
          />
          <Typography
            sx={{
              m: 0,
              color: '#9E9E9E',
              fontSize: '12px'
            }}
          >
            This is automatically calculated and prorated according to how much
            data is used
          </Typography>
        </Box>
      </Box>
      <Box sx={{ pb: 1, pt: 1 }}>
        <FormControl disabled variant='standard' sx={{ width: '100%' }}>
          <InputLabel>Customer Email</InputLabel>
          <Input
            fullWidth
            multiline
            sx={{ mt: 2, borderRadius: '8px' }}
            id='component-disabled'
            defaultValue={customerEmail}
          />
        </FormControl>
      </Box>
      <Box sx={{ pb: 1, pt: 2 }}>
        <FormControl variant='standard' sx={{ width: '100%' }}>
          <InputLabel shrink htmlFor='bootstrap-input'>
            Zendesk Ticket ID*
          </InputLabel>
          <TextField
            name='ticketId'
            onChange={(e) => {
              formik.handleBlur(e)
              formik.handleChange(e)
            }}
            value={formik.values.ticketId}
            autoFocus
            label=''
            id='name'
            type='text'
            fullWidth
            variant='standard'
            required
            sx={{ mt: 2, borderRadius: '8px' }}
            error={formik.touched.ticketId && Boolean(formik.errors.ticketId)}
            helperText={formik.touched.ticketId && formik.errors.ticketId}
          />
        </FormControl>
      </Box>

      <Box sx={{ pb: 1, pt: 2 }}>
        <InputLabel shrink htmlFor='bootstrap-input'>
          Refund Reasons*
        </InputLabel>
        <FormControl variant='standard' sx={{ width: '100%' }}>
          <TextField
            sx={{ borderRadius: '8px' }}
            name='refundReason'
            value={formik.values.refundReason}
            fullWidth
            multiline
            autoFocus
            required
            onChange={(e) => {
              formik.handleBlur(e)
              formik.handleChange(e)
            }}
            error={
              formik.touched.refundReason && Boolean(formik.errors.refundReason)
            }
            helperText={
              formik.touched.refundReason && formik.errors.refundReason
            }
          />
        </FormControl>

        <Box sx={{ pb: 1, pt: 3 }}>
          <InputLabel shrink>Reporter*</InputLabel>
          <TextField
            value={username}
            id='component-disabled'
            name='reporter'
            placeholder='Reporter'
            fullWidth
            variant='outlined'
            inputProps={{ min: 0 }}
            sx={{ mt: 0, borderRadius: '8px' }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Checkbox
            defaultChecked
            onChange={(e) => setIsTerminated(e.target.checked)}
          />
          <Typography
            sx={{
              m: 0,
              color: '#9E9E9E',
              fontSize: '12px'
            }}
          >
            I would like to terminate this pack
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, mt: 0 }}>
          <NormalText
            theme='warning'
            sx={{ fontStyle: 'italic', textAlign: 'center', fontSize: 12 }}
          >
            {`Refund receipt will also be sent to customer automatically upon refund`}
          </NormalText>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, mt: 1 }}>
          <LoadingButton
            loading={isRefundLoading}
            loadingIndicator={
              <CircularProgress size={24} sx={{ color: 'white' }} />
            }
            variant='contained'
            sx={{
              '&.Mui-disabled': {
                border: 'none'
              }
            }}
            disabled={
              !formik.isValid ||
              formik.values.refundReason === '' ||
              formik.values.ticketId === '' ||
              Number(maxRefundAmount) === 0 ||
              totalpackDataUsedPercentage > maximumRefundPercentage
            }
            type='submit'
            onClick={openDialog}
          >
            Submit
          </LoadingButton>
        </Box>
      </Box>
    </Stack>
  )
}

interface RefundFormProps {
  refundType: string
  customerEmail: string
  isRefundLoading: boolean
  amount?: string
  maxRefundAmount: string
  setRefundAmount: (value: string) => void
  currency?: string
  handleRefundLoading: () => void
  customerId: string
  totalOtpackDataUsed: number
  totalpackDataUsedPercentage: number
  maximumRefundPercentage: number
  transactionType: string
  mutateRefund?: ({
    faberToken,
    invoiceID,
    refundReason,
    userId,
    ticketId,
    refundAmount,
    currency,
    terminatePlan
  }) => void
  allocationId?: string
  paymentId?: string
}
